/**
 * layoutConstants.ts - Centralized layout and dimension constants
 *
 * Purpose:
 *   Single source of truth for all layout-related constants used in AutomationFlows.
 *   Ensures consistency across components and reduces magic numbers.
 *
 * Usage:
 *   import { NODE_WIDTH, Z_INDEX, ANIMATION } from '../utils/layoutConstants';
 *
 * Categories:
 *   - Node dimensions: Physical size and spacing of flow nodes
 *   - Edge dimensions: Connection line styling
 *   - Spacing: Gaps and margins
 *   - Z-index layers: Proper element stacking
 *   - Animation timings: Consistent transitions
 *   - Breakpoints: Responsive design thresholds
 *
 * Related:
 *   - utils/layoutEngine.ts
 *   - nodes/stepNodeStyles.ts
 *   - edges/styles.ts
 */

/**
 * Node dimensions and spacing
 * Used by: layoutEngine, stepNodeStyles, node components
 */
export const NODE_WIDTH = 420;
export const NODE_MIN_HEIGHT = 100;
export const NODE_MAX_HEIGHT = 1000; // Increased to allow more content without scrollbar
export const NODE_HORIZONTAL_SPACING = 180;
export const NODE_VERTICAL_SPACING = 120;

/**
 * Branch-specific spacing for switch nodes
 * Used by: convertTemplateToFlow, layoutEngine
 */
export const BRANCH_HORIZONTAL_SPACING = 600; // Space between branches when spread horizontally
export const BRANCH_VERTICAL_SPACING = 400; // Space between branches when spread vertically

/**
 * Flow spacing between sequential nodes
 * Used by: convertTemplateToFlow, layoutAdjustment
 */
export const FLOW_VERTICAL_SPACING = 200; // Space between sequential nodes in vertical layout
export const FLOW_HORIZONTAL_SPACING = 600; // Space between sequential nodes in horizontal layout - increased for better spacing

/**
 * Edge (connection line) dimensions
 * Used by: edge components, layoutEngine
 */
export const EDGE_STROKE_WIDTH = 2;
export const EDGE_LABEL_OFFSET = 20;

/**
 * Standard spacing values
 * Used by: various components for consistent gaps
 */
export const SPACING = {
	XS: 4,
	SM: 8,
	MD: 12,
	LG: 16,
	XL: 20,
	XXL: 24,
} as const;

/**
 * Z-index layers for proper stacking
 * Used by: All components that need layering control
 */
export const Z_INDEX = {
	NODE_BASE: 1,
	NODE_SELECTED: 5,
	BADGE: 10,
	CONTACT_INDICATOR: 10,
	EDGE_LABEL: 15,
	TOOLBAR: 100,
	DRAWER: 200,
	MODAL: 1000,
	TOAST: 2000,
} as const;

/**
 * Animation durations (milliseconds)
 * Used by: Transitions, animations, debouncing
 */
export const ANIMATION = {
	TRANSITION_DURATION: 300,
	TRANSITION_FAST: 150,
	TRANSITION_SLOW: 500,
	DEBOUNCE_DELAY: 150,
	CONTACT_ANIMATION_INTERVAL: 50,
	AUTOSAVE_DELAY: 1000,
} as const;

/**
 * Breakpoints for responsive design
 * Used by: Media queries, responsive layouts
 */
export const BREAKPOINTS = {
	SMALL: 640,
	MEDIUM: 1024,
	LARGE: 1280,
	XLARGE: 1536,
} as const;

/**
 * Trigger node specific dimensions
 * Used by: TriggerNode component
 */
export const TRIGGER_NODE = {
	WIDTH: 200,
	HEIGHT: 80,
} as const;

/**
 * Handle (connection point) dimensions
 * Used by: Node handles for connecting edges
 */
export const HANDLE = {
	SIZE: 12,
	BORDER_WIDTH: 2,
} as const;

/**
 * Badge and indicator dimensions
 * Used by: Contact badges, case indicators, status badges
 */
export const BADGE = {
	SMALL: 8,
	MEDIUM: 12,
	LARGE: 16,
} as const;

/**
 * Border radius values for consistent rounding
 * Used by: Cards, buttons, badges
 */
export const BORDER_RADIUS = {
	SMALL: 4,
	MEDIUM: 6,
	LARGE: 8,
	FULL: 12,
	CIRCLE: '50%',
} as const;

/**
 * Common font sizes
 * Used by: Text elements across components
 */
export const FONT_SIZE = {
	XS: 10,
	SM: 11,
	BASE: 13,
	MD: 14,
	LG: 16,
	XL: 20,
	XXL: 26,
} as const;
